import { MS_PER_DAY, parseIsoDate, toIsoDate } from "./iso-date";
import { currencyFractionDigits, minorToMajor } from "./money";

/**
 * A rate change as the host sent it. The server stores a batch's summary as
 * the structured parts below rather than as a sentence; the host app words
 * them from its own catalog.
 */

/** The night values a change can set, as `rate_calendar` stores them. */
export interface NightValues {
  amountMinor: number | null;
  closedToArrival: boolean;
  closedToDeparture: boolean;
  maxStay: number | null;
  minStay: number | null;
  minStayArrival: number | null;
  minStayThrough: number | null;
  /**
   * What the night sold at once its season and base were folded in, kept on
   * a change record so "was 125, now 333" can name the base a night read
   * before it had a price of its own. Never a column; `rate_calendar` holds
   * the raw override only.
   */
  resolvedAmountMinor?: number | null;
  /**
   * The minimum stay the night enforced, its season's folded in, kept on a
   * change record for the same reason. Never a column.
   */
  resolvedMinStay?: number | null;
  stopSell: boolean;
}

/** A night's values with the record-only fields left out, for a calendar write. */
export function rawNightValues(values: NightValues): NightValues {
  const {
    resolvedAmountMinor: _amount,
    resolvedMinStay: _minStay,
    ...raw
  } = values;
  return raw;
}

/**
 * One set of values to apply over a span.
 *
 * A key that is absent is left as it is. `null` on a price or a stay rule
 * clears it, so the night falls back to its season or the base price, to its
 * season's minimum (or none), and to no maximum. The two are kept apart so a
 * host can take a rule off a stretch of nights.
 */
export interface RateChangeFields {
  amountMinor?: number | null;
  closedToArrival?: boolean;
  closedToDeparture?: boolean;
  maxStay?: number | null;
  minStay?: number | null;
  stopSell?: boolean;
}

/** A night with nothing set: what `rate_calendar` holds for a night with no row. */
export const EMPTY_NIGHT_VALUES: NightValues = {
  amountMinor: null,
  closedToArrival: false,
  closedToDeparture: false,
  maxStay: null,
  minStay: null,
  minStayArrival: null,
  minStayThrough: null,
  stopSell: false,
};

/**
 * A night after one line's fields land on it. A key that is absent leaves
 * the value; null clears it. Setting the minimum stay also clears the two
 * explicit per-field minimums, so the host's one number is what the push
 * carries on both wire fields. The server writes with this and the board
 * overlays with it until the written row syncs down, so the two agree.
 */
export function applyFieldsToNight(
  prior: NightValues | null,
  fields: RateChangeFields
): NightValues {
  const next: NightValues = { ...(prior ?? EMPTY_NIGHT_VALUES) };
  if (fields.amountMinor !== undefined) {
    next.amountMinor = fields.amountMinor;
  }
  if (fields.minStay !== undefined) {
    next.minStay = fields.minStay;
    next.minStayArrival = null;
    next.minStayThrough = null;
  }
  if (fields.maxStay !== undefined) {
    next.maxStay = fields.maxStay;
  }
  if (fields.closedToArrival !== undefined) {
    next.closedToArrival = fields.closedToArrival;
  }
  if (fields.closedToDeparture !== undefined) {
    next.closedToDeparture = fields.closedToDeparture;
  }
  if (fields.stopSell !== undefined) {
    next.stopSell = fields.stopSell;
  }
  return next;
}

export const RATE_CHANGE_FIELD_KEYS = [
  "amountMinor",
  "minStay",
  "maxStay",
  "closedToArrival",
  "closedToDeparture",
  "stopSell",
] as const satisfies readonly (keyof RateChangeFields)[];

/** One span of one unit type in a send. */
export interface RateChangeLine {
  fields: RateChangeFields;
  /** Inclusive first night, `YYYY-MM-DD`. */
  from: string;
  nights: number;
  /** Inclusive last night, `YYYY-MM-DD`. */
  to: string;
  unitTypeId: string;
}

/** One unit type's units-free count over a span, as pushed. */
export interface AvailabilityPushLine {
  availability: number;
  from: string;
  to: string;
  unitTypeId: string;
}

export type RateChangeSummary =
  | {
      kind: "rates";
      lines: RateChangeLine[];
      /** The batch this one reversed, when it is an undo. */
      undoOf?: string;
    }
  | {
      kind: "availability";
      lines: AvailabilityPushLine[];
      /** What made the outbox push: a booking or block, or the nightly refresh. */
      trigger: "booking" | "nightly_refresh";
    };

export const RATE_CHANGE_DELIVERY_STATES = [
  "queued",
  "sending",
  "retrying",
  "awaiting_full_sync",
  "sent",
  "sent_later",
  "not_sent",
  "not_connected",
  "not_mapped",
  "passed_unanswered",
] as const;
export type RateChangeDeliveryState =
  (typeof RATE_CHANGE_DELIVERY_STATES)[number];

/**
 * What the channel manager refused when it ended a send not sent: Vidimo's
 * access (`connection`) or the values (`values`).
 */
export const rateChangeNotSentReasonValues = ["connection", "values"] as const;
export type RateChangeNotSentReason =
  (typeof rateChangeNotSentReasonValues)[number];

/** States the drain is still working on: it has not answered yet. */
export const RATE_CHANGE_IN_FLIGHT_STATES: readonly RateChangeDeliveryState[] =
  ["queued", "sending", "retrying"];

/** How delivery started for one unit type, decided when the outbox was marked. */
export type RateChangeDeliveryStart = "queued" | "not_connected" | "not_mapped";

export interface RateChangeRoomTypeOutcome {
  delivery: RateChangeDeliveryStart;
  nights: number;
  /**
   * Sent again on its own after the rest of the change had reached the
   * channel, so a further Send again carries it and nothing else.
   */
  resent?: boolean;
  unitTypeId: string;
}

// Words

const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/**
 * A nightly amount the way the board prints it: `120`, or `312.66` when the
 * cents matter. No currency symbol, because the column header names the
 * currency once and a symbol in every cell is what pushes `312.66` into a
 * second line at 390pt.
 */
export function formatRateAmount(
  amountMinor: number,
  currency: string
): string {
  const { amount, fractionDigits } = rateAmountReading(amountMinor, currency);
  return amount.toFixed(fractionDigits);
}

/** The amount {@link formatRateAmount} prints and its decimals: none when whole. */
export function rateAmountReading(
  amountMinor: number,
  currency: string
): { amount: number; fractionDigits: number } {
  const amount = minorToMajor(amountMinor, currency);
  return {
    amount,
    fractionDigits: Number.isInteger(amount)
      ? 0
      : currencyFractionDigits(currency),
  };
}

/** Why a lowest price cannot be saved: it is not a price above 0. */
export const LOWEST_PRICE_NOT_ABOVE_0 = "Enter a lowest price above 0.";

/** Why a lowest price cannot be saved: it is over the base rate. */
export const LOWEST_PRICE_OVER_BASE =
  "The lowest price cannot be above the base rate.";

/**
 * `2 nights and 1 season of Double are priced under 100. Raise them first,
 * then set the lowest price.`: why a lowest price cannot rise over prices
 * still ahead. The server refuses the save with it, and Settings says it
 * before the save.
 */
export function pricedUnderWords(args: {
  amount: string;
  nights: number;
  seasons: number;
  unitTypeName: string | undefined;
}): string {
  const { amount, nights, seasons, unitTypeName } = args;
  const parts = [
    nights > 0 ? `${nights} ${nights === 1 ? "night" : "nights"}` : null,
    seasons > 0 ? `${seasons} ${seasons === 1 ? "season" : "seasons"}` : null,
  ].filter((part): part is string => part !== null);
  const isOne = nights + seasons === 1;
  const ofUnitType = unitTypeName ? ` of ${unitTypeName}` : "";
  return `${parts.join(" and ")}${ofUnitType} ${isOne ? "is" : "are"} priced under ${amount}. Raise ${isOne ? "it" : "them"} first, then set the lowest price.`;
}

/** Why a nightly price cannot be set: not above 0, or under the lowest price. */
export type NightlyPriceRefusal =
  | { kind: "notAbove0" }
  | { floorMinor: number; kind: "belowFloor" };

/**
 * Why a nightly price cannot be set, or null when it can. A price is above 0,
 * because a free night is one anybody can book (a promotion is a single-use
 * code, never a price), and at or above the unit type's lowest price when its
 * owner has set one. One rule for the Rates view, the base rate, seasons and
 * every server path that writes a price, so none of them can slip under it.
 */
export function nightlyPriceRefusal(args: {
  amountMinor: number;
  floorMinor: number | null | undefined;
}): NightlyPriceRefusal | null {
  const { amountMinor, floorMinor } = args;
  if (!(amountMinor > 0)) {
    return { kind: "notAbove0" };
  }
  const belowFloor = typeof floorMinor === "number" && amountMinor < floorMinor;
  return belowFloor ? { floorMinor, kind: "belowFloor" } : null;
}

/** `nightlyPriceRefusal` in the server's words. */
export function nightlyPriceProblem(args: {
  amountMinor: number;
  currency: string;
  floorMinor: number | null | undefined;
  /** Named in the message, when known: `The lowest price for Double is 90.` */
  unitTypeName?: string;
}): string | null {
  const { currency, unitTypeName } = args;
  const refusal = nightlyPriceRefusal(args);
  if (refusal === null) {
    return null;
  }
  if (refusal.kind === "notAbove0") {
    return "Enter a price above 0.";
  }
  const floor = formatRateAmount(refusal.floorMinor, currency);
  return unitTypeName
    ? `The lowest price for ${unitTypeName} is ${floor}.`
    : `The lowest price here is ${floor}.`;
}

/** `Sun 22 Nov`, in the English the API writes its own sentences in. */
export function dayWords(iso: string): string {
  const d = parseIsoDate(iso);
  return `${DOW[d.getUTCDay()]} ${d.getUTCDate()} ${MONTHS_SHORT[d.getUTCMonth()]}`;
}

/** Inclusive night count of a span, or 0 when either end is missing. */
export function countNights(from: string, to: string): number {
  const a = parseIsoDate(from).getTime();
  const b = parseIsoDate(to).getTime();
  if (Number.isNaN(a) || Number.isNaN(b) || b < a) {
    return 0;
  }
  return Math.round((b - a) / MS_PER_DAY) + 1;
}

/**
 * Every night of an inclusive span, `YYYY-MM-DD` each. The server writes the
 * nights this lists and the board paints the same list until the rows sync
 * down, so the two never disagree on which nights a span covers.
 */
export function nightsOf(from: string, to: string): string[] {
  const start = parseIsoDate(from).getTime();
  return Array.from({ length: countNights(from, to) }, (_, i) =>
    toIsoDate(new Date(start + i * MS_PER_DAY))
  );
}
